// @ts-nocheck

class ComingSoonInquiryButton extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    this.initializeModal();
    this.maybeResumeAfterCreate();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.cleanupModalEventListeners(document.querySelector('coming-soon-inquiry-form-modal'));
  }

  handleClick(event) {
    event.preventDefault();
    this.openModal();
  }

  initializeModal() {
    const modal = document.querySelector('coming-soon-inquiry-form-modal');
    if (!modal || modal.dataset.listenersInitialized) return;

    const closeButton = modal.querySelector('.coming-soon-inquiry-form-modal__close-button');
    if (closeButton) {
      this.closeButtonHandler = () => this.hideModal(modal);
      closeButton.addEventListener('click', this.closeButtonHandler);
    }

    const overlay = modal.querySelector('.coming-soon-inquiry-form-modal__overlay');
    if (overlay) {
      this.overlayHandler = () => this.hideModal(modal);
      overlay.addEventListener('click', this.overlayHandler);
    }

    this.keydownHandler = (e) => {
      if (e.key === 'Escape' && modal.classList.contains('active')) this.hideModal(modal);
    };
    document.addEventListener('keydown', this.keydownHandler);

    const form = modal.querySelector('#coming-soon-inquiry-form');
    if (form) {
      this.formSubmitHandler = (e) => this.handleSubmit(e, modal);
      form.addEventListener('submit', this.formSubmitHandler);
    }

    modal.dataset.listenersInitialized = 'true';
  }

  cleanupModalEventListeners(modal) {
    if (!modal) return;

    const closeButton = modal.querySelector('.coming-soon-inquiry-form-modal__close-button');
    if (closeButton && this.closeButtonHandler) closeButton.removeEventListener('click', this.closeButtonHandler);

    const overlay = modal.querySelector('.coming-soon-inquiry-form-modal__overlay');
    if (overlay && this.overlayHandler) overlay.removeEventListener('click', this.overlayHandler);

    if (this.keydownHandler) document.removeEventListener('keydown', this.keydownHandler);

    const form = modal.querySelector('#coming-soon-inquiry-form');
    if (form && this.formSubmitHandler) form.removeEventListener('submit', this.formSubmitHandler);
  }

  splitOptions(value) {
    return (value || '')
      .split('~~|~~')
      .map((v) => v.trim())
      .filter(Boolean);
  }

  populateSelect(select, values) {
    if (!select) return;
    select.querySelectorAll('option:not([value=""])').forEach((option) => option.remove());
    (values.length ? values : ['N/A']).forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.appendChild(option);
    });
  }

  openModal() {
    const modal = document.querySelector('coming-soon-inquiry-form-modal');
    if (!modal) {
      console.error('Coming soon inquiry modal not found');
      return;
    }

    const sheetUrl = this.dataset.sheetUrl || '';
    const currentColor = this.dataset.currentColor || '';

    this.populateSelect(modal.querySelector('#csi-size'), this.splitOptions(this.dataset.availableSizes));

    const form = modal.querySelector('#coming-soon-inquiry-form');
    if (form) {
      form.dataset.sheetUrl = sheetUrl;
      const setValue = (id, value) => {
        const el = form.querySelector(`#${id}`);
        if (el) el.value = value;
      };
      setValue('csi-page-url', window.location.href);
      setValue('csi-color', currentColor);
      form.querySelector('#csi-size').value = '';
    }

    this.showModal(modal);
  }

  showModal(modal) {
    modal.classList.add('active');
    const container = modal.querySelector('.coming-soon-inquiry-form-modal__container');
    if (container) {
      container.classList.remove('closing');
      container.classList.add('active');
    }
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      const nameInput = modal.querySelector('#csi-name');
      if (nameInput) nameInput.focus();
    }, 100);
  }

  hideModal(modal) {
    const container = modal.querySelector('.coming-soon-inquiry-form-modal__container');
    if (container) {
      container.classList.remove('active');
      container.classList.add('closing');
    }
    modal.classList.remove('active');
    document.body.style.overflow = '';
  }

  handleSubmit(event, modal) {
    if (this.isSubmitting) {
      event.preventDefault();
      return;
    }

    const form = event.target;
    if (!form.checkValidity()) {
      event.preventDefault();
      form.reportValidity();
      return;
    }

    // Validation passed — stash the payload (posted to the sheet after the reload
    // below completes, so it isn't racing the navigation), populate the customer
    // fields, then let the native form submit proceed. No event.preventDefault():
    // the form is {%- form 'create_customer' -%} and submits natively.
    const payload = this.buildPayload(form);
    sessionStorage.setItem('csiPendingPayload', JSON.stringify(payload));
    this.populateCustomerFields(payload, form);

    this.setSubmitting(form, true);
    this.isSubmitting = true;
    this.hideModal(modal);
  }

  setSubmitting(form, isLoading) {
    const submitButton = form.querySelector('.coming-soon-inquiry-form-modal__submit');
    const submitButtonText = form.querySelector('.coming-soon-inquiry-form-modal__submit-text');
    if (!submitButton) return;

    if (submitButtonText) submitButtonText.textContent = isLoading ? 'Submitting...' : 'Submit';
    submitButton.disabled = isLoading;
  }

  buildPayload(form) {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const dateTime = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} - ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

    return {
      sheetUrl: form.dataset.sheetUrl || '',
      name: (form.querySelector('#csi-name')?.value || '').trim(),
      email: (form.querySelector('#csi-email')?.value || '').trim().toLowerCase(),
      size: form.querySelector('#csi-size')?.value || '',
      color: form.querySelector('#csi-color')?.value || '',
      pageUrl: form.querySelector('#csi-page-url')?.value || window.location.href,
      dateTime,
    };
  }

  populateCustomerFields(payload, form) {
    const parts = payload.name.split(/\s+/).filter(Boolean);
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ');
    const password = `Csi-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

    const tags = ['coming-soon-inquiry', 'source-coming-soon-inquiry'];
    [payload.size, payload.color].forEach((value) => {
      if (value) tags.push(value.toLowerCase().replace(/\s+/g, '-'));
    });

    const set = (id, value) => {
      const el = form.querySelector(`#${id}`);
      if (el) el.value = value;
    };
    set('csi-first-name', firstName);
    set('csi-last-name', lastName);
    set('csi-tags', tags.join(','));
    set('csi-password', password);
    set('csi-password-confirm', password);
  }

  postToSheet(payload) {
    if (!payload.sheetUrl) {
      console.warn(
        '[coming-soon-inquiry] No Google Apps Script URL configured in the theme editor — skipping sheet post.',
      );
      return Promise.resolve();
    }

    const body = JSON.stringify({
      source: 'coming-soon-inquiry',
      name: payload.name,
      email: payload.email,
      size: payload.size,
      color: payload.color,
      pageUrl: payload.pageUrl,
      dateTime: payload.dateTime,
    });

    return fetch(payload.sheetUrl, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body,
    });
  }

  maybeResumeAfterCreate() {
    const raw = sessionStorage.getItem('csiPendingPayload');
    if (!raw) return;
    sessionStorage.removeItem('csiPendingPayload');

    let payload;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }

    this.postToSheet(payload)
      .then(() => this.showSuccessToast())
      .catch((err) => console.warn('[coming-soon-inquiry] Sheet post failed on resume:', err));
  }

  showSuccessToast() {
    const toast = document.createElement('div');
    toast.className = 'csi-toast';
    toast.textContent = "Thanks! We'll be in touch with more details.";
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 4500);
  }
}

if (!customElements.get('coming-soon-inquiry-button')) {
  customElements.define('coming-soon-inquiry-button', ComingSoonInquiryButton);
}

// Safety net: if the drawer was left open when the user navigated away (back/forward,
// closing the tab), a bfcache restore can bring back the page with body scroll still
// locked and no way to close it. Clear the lock unless the drawer is genuinely active.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  const modal = document.querySelector('coming-soon-inquiry-form-modal');
  if (modal && !modal.classList.contains('active')) {
    document.body.style.overflow = '';
  }
});
